import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import dayjs, { Dayjs } from "dayjs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import UndoIcon from "@mui/icons-material/Undo";
import ExportButton from "../components/ExportButton";
import CustomerDrawer from "../components/Customer/CustomerDrawer";
import PurchaseOrderDetailDrawer from "../components/PurchaseOrder/DetailDrawer";
import PurchaseOrderFilter from "../components/PurchaseOrder/Filter";
import ReturnDialog from "../components/PurchaseOrder/ReturnDialog";
import PurchaseOrderTable from "../components/PurchaseOrder/Table";
import UnmatchedBanner from "../components/PurchaseOrder/UnmatchedBanner";
import WeeklyRouteGrid from "../components/PurchaseOrder/WeeklyRouteGrid";
import {
  isReturnable,
  PO_API_DATE_FORMAT,
  PO_DELIVERY_RANGE_BACK_DAYS,
  PO_DELIVERY_RANGE_FORWARD_DAYS,
  PO_SEARCH_DEBOUNCE_MS,
  PO_UNFILTERED,
  toChainFilter,
  toStatusFilter,
} from "../components/PurchaseOrder/data";
import { usePurchaseOrderList } from "../components/PurchaseOrder/usePurchaseOrderList";
import { featureAccess } from "../permissions/access";
import { exportPurchaseOrders } from "../services";
import {
  PurchaseOrderListFilter,
  PurchaseOrderListItem,
  PurchaseOrderStatus,
} from "../types/purchase-order.type";
import { useDebounce } from "../utils/hooks/useDebounce";
import { useUserRole } from "../utils/hooks/useUserRole";

/** สถานะที่แถบแดงใช้กรอง — งานที่ค้างอยู่ที่สาขา */
const UNMATCHED_STATUS: PurchaseOrderStatus = "UNMATCHED_CUSTOMER";

const TAB_LIST = "list";
const TAB_WEEKLY = "weekly";
type BranchPurchaseOrderTab = typeof TAB_LIST | typeof TAB_WEEKLY;

/**
 * W7 PO ของสาขา
 *
 * ตารางเดียวกับ W1 แต่ถูก backend ปักไว้ที่สาขาของ token และเห็นเฉพาะใบที่ส่งมาแล้ว
 * จัดกลุ่มตามสายที่อ่านจากร้าน · งานหลักของจอนี้คือจับคู่ร้านที่ค้าง ซึ่งต้องมีคนเลือก
 * และกดบันทึกทุกใบ ไม่มีการจับคู่ให้อัตโนมัติ
 *
 * แท็บ "ตามสาย × วัน" สรุปทั้งสัปดาห์ว่าแต่ละสายมี PO ส่งวันไหน กี่ใบ กี่ร้าน (แกนวัน =
 * วันส่ง) · HO ที่เข้าจอนี้ต้องเลือกสาขาก่อน เพราะเลขสายซ้ำกันข้ามสาขา
 */
export default function BranchPurchaseOrderPage() {
  const role = useUserRole();
  // HO (admin/master) เห็นทุกสาขา จึงต้องมีช่องเลือกสาขาในมุมมองรายสัปดาห์
  const isHeadOffice = featureAccess.poManage(role);
  const [tab, setTab] = useState<BranchPurchaseOrderTab>(TAB_LIST);
  // mount มุมมองรายสัปดาห์ครั้งแรกที่เปิดแท็บ แล้วคงไว้ สัปดาห์ที่เลือกจึงไม่หายเมื่อสลับแท็บ
  const [weeklyVisited, setWeeklyVisited] = useState<boolean>(false);
  const [dateFrom, setDateFrom] = useState<Dayjs>(() =>
    dayjs().startOf("day").subtract(PO_DELIVERY_RANGE_BACK_DAYS, "day"),
  );
  const [dateTo, setDateTo] = useState<Dayjs>(() =>
    dayjs().startOf("day").add(PO_DELIVERY_RANGE_FORWARD_DAYS, "day"),
  );
  const [chain, setChain] = useState<string>(PO_UNFILTERED);
  const [status, setStatus] = useState<string>(PO_UNFILTERED);
  const [search, setSearch] = useState<string>("");
  const [detailUuid, setDetailUuid] = useState<string | null>(null);
  const [newCustomerOpen, setNewCustomerOpen] = useState<boolean>(false);
  // เพิ่มค่าเมื่อมีร้านใหม่ถูกบันทึก เพื่อให้ dropdown จับคู่ร้านอ่านตัวเลือกใหม่
  const [candidatesRefreshToken, setCandidatesRefreshToken] =
    useState<number>(0);
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(
    () => new Set(),
  );
  // ใบที่กำลังจะส่งคืน · null = dialog ปิดอยู่
  const [returnTargets, setReturnTargets] = useState<
    PurchaseOrderListItem[] | null
  >(null);
  // เพิ่มค่าหลังส่งคืน เพื่อให้ตารางสาย × วันอ่านตัวเลขใหม่
  const [weeklyRefreshToken, setWeeklyRefreshToken] = useState<number>(0);

  // ส่งคืน HO เป็นงานของผู้ใช้สาขา (API รับเฉพาะ branch roles) · HO ที่เปิดจอนี้ไม่เห็นปุ่ม
  const canReturn = !isHeadOffice;

  const debouncedSearch = useDebounce(search, PO_SEARCH_DEBOUNCE_MS);

  // ตัวกรองชุดเดียวของตารางและของ Export · ไม่ส่ง company_id เพราะ backend ปักสาขา
  // จาก token ให้อยู่แล้ว
  const filter = useMemo<PurchaseOrderListFilter>(
    () => ({
      delivery_from: dateFrom.format(PO_API_DATE_FORMAT),
      delivery_to: dateTo.format(PO_API_DATE_FORMAT),
      chain: toChainFilter(chain),
      status: toStatusFilter(status),
      q: debouncedSearch || undefined,
    }),
    [dateFrom, dateTo, chain, status, debouncedSearch],
  );

  const list = usePurchaseOrderList(filter);
  const { rows, reload, replaceRow } = list;

  // ติ๊กเลือกได้เฉพาะใบในหน้าที่แสดงอยู่ · ชุดแถวเปลี่ยนแล้วล้างทิ้งเหมือน W1
  useEffect(() => {
    setSelectedUuids(new Set());
  }, [rows]);

  const selectedReturnable = useMemo(
    () => rows.filter((row) => selectedUuids.has(row.uuid) && isReturnable(row)),
    [rows, selectedUuids],
  );

  const selection = useMemo(
    () =>
      canReturn
        ? {
            selected: selectedUuids,
            onChange: setSelectedUuids,
            canSelect: isReturnable,
            // เลือกได้เฉพาะใบพร้อมใช้ · ใบอื่นบอกเหตุผลที่ส่งคืนไม่ได้
            disabledReason: (row: PurchaseOrderListItem) =>
              row.status === "USED"
                ? "ใช้แล้ว"
                : row.status === "EXPIRED"
                  ? "เลยช่วงวันส่ง"
                  : "ส่งคืนได้เฉพาะใบพร้อมใช้ (จับคู่ร้านแล้ว)",
          }
        : undefined,
    [canReturn, selectedUuids],
  );

  const showingOnlyUnmatched = status === UNMATCHED_STATUS;

  const handleChangeDateRange = useCallback((from: Dayjs, to: Dayjs) => {
    setDateFrom(from);
    setDateTo(to);
  }, []);

  const handleToggleUnmatchedOnly = () => {
    setStatus(showingOnlyUnmatched ? PO_UNFILTERED : UNMATCHED_STATUS);
  };

  const handleExport = () => exportPurchaseOrders(filter);

  const handleOpenDetail = (uuid: string) => {
    setDetailUuid(uuid);
  };

  const handleCloseDetail = () => {
    setDetailUuid(null);
  };

  const handleOpenBulkReturn = () => {
    setReturnTargets(selectedReturnable);
  };

  const handleReturnRow = useCallback((row: PurchaseOrderListItem) => {
    setReturnTargets([row]);
  }, []);

  const handleCloseReturn = () => {
    setReturnTargets(null);
  };

  // ใบที่ส่งคืนออกจากสาขาไปแล้ว — ปิด drawer ที่อาจเปิดค้าง และอ่านทั้งสองมุมมองใหม่
  const handleReturned = () => {
    setDetailUuid(null);
    reload(true);
    setWeeklyRefreshToken((previous) => previous + 1);
  };

  const handleChangeTab = (
    _event: SyntheticEvent,
    value: BranchPurchaseOrderTab,
  ) => {
    setTab(value);
    if (value === TAB_WEEKLY) setWeeklyVisited(true);
  };

  // แทนที่แถวด้วยค่าที่ API คืนมาทันที เพื่อไม่ให้แผงจับคู่ร้านใบอื่นที่เปิดค้างอยู่หาย
  // แล้วค่อยอ่านรายการซ้ำแบบไม่ขึ้น skeleton ให้แถบ "ยังไม่จับคู่ร้าน N ใบ" กับจำนวนหน้า
  // กลับมาตรงกับของจริง — ตัวเลขทั้งสองค่ามาจาก API ไม่ได้นับเองในเบราว์เซอร์
  const handleMatched = useCallback(
    (row: PurchaseOrderListItem) => {
      replaceRow(row);
      reload(false);
    },
    [replaceRow, reload],
  );

  const handleOpenNewCustomer = useCallback(() => {
    setNewCustomerOpen(true);
  }, []);

  const handleCloseNewCustomer = () => {
    setNewCustomerOpen(false);
  };

  const handleNewCustomerSaved = () => {
    setCandidatesRefreshToken((previous) => previous + 1);
  };

  const matching = useMemo(
    () => ({
      refreshToken: candidatesRefreshToken,
      onMatched: handleMatched,
      onOpenNewCustomer: handleOpenNewCustomer,
    }),
    [candidatesRefreshToken, handleMatched, handleOpenNewCustomer],
  );

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Typography variant="h6" fontWeight={700}>
          PO ของสาขา
        </Typography>

        {tab === TAB_LIST && (
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <ExportButton
              request={handleExport}
              fallbackFilename={`purchase-orders_${dayjs().format("YYYYMMDD")}.xlsx`}
              disabled={list.totalCount === 0}
            />
            {canReturn && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<UndoIcon />}
                disabled={selectedReturnable.length === 0}
                onClick={handleOpenBulkReturn}
              >
                ส่งคืน HO ({selectedReturnable.length})
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      <Tabs value={tab} onChange={handleChangeTab} sx={{ mb: 2 }}>
        <Tab value={TAB_LIST} label="รายการ" />
        <Tab value={TAB_WEEKLY} label="ตามสาย × วัน" />
      </Tabs>

      {/* ซ่อนแทนการถอดออก ตัวกรองและหน้าของแท็บรายการจึงยังอยู่เมื่อสลับกลับมา */}
      <Box hidden={tab !== TAB_LIST}>
        <UnmatchedBanner
          count={list.summary.UNMATCHED_CUSTOMER}
          showingOnlyUnmatched={showingOnlyUnmatched}
          onToggleUnmatchedOnly={handleToggleUnmatchedOnly}
        />

        {/* ไม่ส่ง branchFilter: สาขาถูกปักจาก token ฝั่ง server อยู่แล้ว */}
        <Paper sx={{ padding: "24px 16px 8px", mb: 2 }}>
          <PurchaseOrderFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChangeDateRange={handleChangeDateRange}
            chain={chain}
            onChangeChain={setChain}
            status={status}
            onChangeStatus={setStatus}
            search={search}
            onChangeSearch={setSearch}
          />
        </Paper>

        <PurchaseOrderTable
          rows={list.rows}
          loading={list.loading}
          totalCount={list.totalCount}
          page={list.page}
          limit={list.limit}
          onChangePage={list.changePage}
          onChangeLimit={list.changeLimit}
          onOpenDetail={handleOpenDetail}
          groupByRoute
          matching={matching}
          selection={selection}
          onReturnRow={canReturn ? handleReturnRow : undefined}
        />
      </Box>

      {weeklyVisited && (
        <Box hidden={tab !== TAB_WEEKLY}>
          <WeeklyRouteGrid
            selectBranch={isHeadOffice}
            onOpenDetail={handleOpenDetail}
            refreshToken={weeklyRefreshToken}
          />
        </Box>
      )}

      <PurchaseOrderDetailDrawer
        purchaseOrderUuid={detailUuid}
        onClose={handleCloseDetail}
        onRequestReturn={canReturn ? handleReturnRow : undefined}
      />

      <ReturnDialog
        open={returnTargets !== null}
        purchaseOrders={returnTargets ?? []}
        onClose={handleCloseReturn}
        onReturned={handleReturned}
      />

      {/* ฟอร์มลูกค้าเดิม เปิดแบบสร้างใหม่ (customerId ว่าง) · สาขาเปิดร้านเองได้ */}
      <CustomerDrawer
        customerId=""
        open={newCustomerOpen}
        onClose={handleCloseNewCustomer}
        onSaved={handleNewCustomerSaved}
      />
    </Box>
  );
}
